import { financialProtectionErrorMessage } from '../../shared/models/financial-protection';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { AssignEmployeeService } from '../../services/assign-employee.service';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { EmployeeAssignStep } from '../../shared/assign-employee/employee-assign-step';
import { ActivatedRoute, Router } from '@angular/router';
import { ProfessionService } from '../../../core/profession.service';
import { PhaseWithEmployees } from '../domain/phase-with-employees';
import { NotificationService } from '../../../core/notification.service';
import { ProjectService } from '../../services/project.service';
import {PhaseAndEmployees} from "../domain/phase-and-employees";

@Component({
  selector: 'app-assign-employee',

  standalone: false,
  template: `
    <ng-container [ngSwitch]="step$.asObservable() | async">
      <ng-container *ngIf="project$ | async as project">
        <app-phases-step
          *ngSwitchCase="'phases'"
          [isPreviousStep]="false"
          [isNextStep]="true"
          [phases]="project.phases"
          [phasesWithEmployees]="phasesWithEmployees$ | async"
          (closeEmployeeAssign)="onCloseEmployeeAssign()"
          (phaseSelected)="onPhaseSelected($event, project.phases)"
        ></app-phases-step>
      </ng-container>
      <app-employees-step
        *ngSwitchCase="'employees'"
        [isPreviousStep]="true"
        [professions]="professions$ | async"
        [projectId]="(project$ | async)?.id"
        [phase]="phase$ | async"
        (closeEmployeeAssign)="onCloseEmployeeAssign()"
        (backToPreviousStep)="setStep('phases')"
        (employeeSelected)="onEmployeeSelected($event)"
      ></app-employees-step>
    </ng-container>
  `,
})
export class AssignEmployeeFromProjectComponent implements OnInit {
  step$ = new BehaviorSubject<EmployeeAssignStep>('phases');

  professions$ = this.professionService.professions$;
  phasesWithEmployees$ = new BehaviorSubject<PhaseWithEmployees[]>([]);

  project$ = this.assignEmployeeService.project$;
  phase$ = this.assignEmployeeService.phase$;
  employeeIds$ = this.assignEmployeeService.employeeIds$;

  constructor(
    private readonly assignEmployeeService: AssignEmployeeService,
    private readonly router: Router,
    private readonly professionService: ProfessionService,
    private readonly notificationService: NotificationService,
    private readonly route: ActivatedRoute,
    private readonly projectService: ProjectService,
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        take(1),
        map((paramMap) => paramMap.get('projectId')),
        switchMap((projectId) => {
          return this.projectService.getProject(projectId).pipe(
            tap((project) => this.assignEmployeeService.setProject(project)),
            switchMap(() => this.projectService.getProjectPhasesWithEmployees(projectId)),
          );
        })
      )
      .subscribe((phasesWithEmployees: PhaseWithEmployees[]) => {
        this.phasesWithEmployees$.next(phasesWithEmployees);
      });
  }

  setStep(value: EmployeeAssignStep): void {
    this.step$.next(value);
  }

  onCloseEmployeeAssign(): void {
    this.route.url
      .pipe(take(1))
      .subscribe((url) => {
        if (url.some((segment) => segment.path === 'project-details'))
          this.navigateToProjectDetails();
        else if (url.some((segment) => segment.path === 'list'))
          this.navigateToProjectList();
      });
  }

  onPhaseSelected(phase: PhaseWithEmployees, otherPhases: PhaseAndEmployees[]): void {
    const matchingPhase = otherPhases.find(p => p.id === phase.id);

    if (matchingPhase && matchingPhase.requiredProfessions) {
      phase.requiredProfessions = matchingPhase.requiredProfessions;
    }

    this.assignEmployeeService.setPhase(phase);
    this.setStep('employees');
  }

  onEmployeeSelected(employeeIds: string[]): void {
    this.assignEmployeeService.setEmployeeIds(employeeIds);

    combineLatest([this.project$, this.phase$, this.employeeIds$])
      .pipe(
        take(1),
        map(([project, phase, employeeIds]): [string, PhaseWithEmployees, string[]] =>
          [project.id, phase, employeeIds]
        ),
      )
      .subscribe(([projectId, phase, employeeIds]) =>
          this.assignEmployeeToPhase(projectId, phase, employeeIds)
      )
  }

  private assignEmployeeToPhase(projectId: string, phase: PhaseWithEmployees, employeeIds: string[]) {
    const before = { ...phase, employees: phase.employees?.map(employee => ({ ...employee })) };
    const requests = employeeIds.map(employeeId => ({ employeeId }));
    this.assignEmployeeService.assignEmployeeToPhase(<string[]>employeeIds, <string>projectId, phase.id)
      .pipe(take(1))
      .subscribe({
        next: phases => {
          this.projectService.refreshProject(projectId);
          const result = this.assignEmployeeService.assignmentDateResult(before, phases, phase.id, requests);
          this.notificationService.setNotification(`Employee assigned.${result.message}`, '', result.adjusted || result.unverified ? { severity: 'warning' } : { duration: 15000 });
          this.onCloseEmployeeAssign();
        },
        error: (response) => {
          const financialMessage = financialProtectionErrorMessage(response, undefined, employeeIds.length > 1 ? 'bulk' : 'single');
          const msg = financialMessage || response?.error?.msg || response.message || 'Error occurred';
          const savedDatesUnavailable = response.message === 'The employees were assigned, but we couldn’t display their saved work dates. Refresh the project to check the assignments. Do not submit them again.';
          this.notificationService.setNotification(msg, '', { severity: savedDatesUnavailable || response?.error?.assignedCount > 0 ? 'warning' : 'error' });
          if (response?.error?.assignedCount > 0) this.projectService.refreshProject(projectId);
        },
      });
  }

  private navigateToProjectDetails(): void {
    this.project$
      .pipe(
        take(1),
        map((project => project.id)),
      )
      .subscribe((projectId) =>
        this.router.navigate(['/main', 'projects', 'project-details', projectId]),
      );
  }

  private navigateToProjectList(): void {
    this.router.navigate(['/main', 'projects', 'list']);
  }
}
